import { Component, Element, h, Host, Prop, State, Event, EventEmitter, Method, Listen } from '@stencil/core';

@Component({
    tag: 'sew-picture',
    styleUrl: 'sew-picture.scss',
    shadow: true,
})
export class SewPicture {
    @Element() host: HTMLSewPictureElement;

    @Prop({ attribute: 'img-src' }) src;
    @Prop({ attribute: 'img-alt' }) alt;
    @Prop({ attribute: 'img-title' }) imgTitle;
    @Prop({ reflect: true }) caption: string;
    @Prop() shiftCaptionRight = false;
    @Prop({ attribute: 'shift-caption-right-mq3' }) shiftCaptionRightMq3 = false;
    @Prop() width?;
    @Prop() height?;
    @Prop() objectFit = false;
    @Prop() objectFitContain = false;
    @Prop() showImage = false;
    @Prop() sewPictureComplete = false;
    @Event() sewPictureLoaded: EventEmitter<void>;

    @State() mediaSources;

    private imgElement: HTMLImageElement;

    @Listen('resize', { target: 'window' })
    updateCurrentRatio() {
        if (this.objectFit || this.objectFitContain) return;

        const currentSrc = this.imgElement.currentSrc;
        const currentRatio = this.getRatio(currentSrc);
        this.setRatio(currentRatio);
    }

    setInitialRatio() {
        if (this.objectFit || this.objectFitContain) return;
        const currentSrc = this.getCurrentSrc();
        const currentRatio = this.getRatio(currentSrc);
        this.setRatio(currentRatio);
    }

    componentWillLoad(): void {
        this.mediaSources = Array.from(this.host.children).filter(child => child.nodeName === 'SOURCE');

        this.setInitialRatio();

        if (window.matchMedia('print').matches) {
            this.showImage = true;
        }
    }

    componentDidLoad(): void {
        if (this.objectFit || this.objectFitContain) return;

        this.imgElement.addEventListener('load', () => {
            this.host.style.removeProperty('aspect-ratio');
        });
    }

    setRatio(ratio: string) {
        this.host.style.aspectRatio = ratio;
    }

    getCurrentSrc() {
        let matchedSrc = '';

        this.mediaSources.forEach(source => {
            const media = source.getAttribute('media');
            const srcset = source.getAttribute('srcset');

            if (media && window.matchMedia(media).matches) {
                matchedSrc = srcset;
            }
        });

        if (!matchedSrc) {
            matchedSrc = this.src;
        }

        return matchedSrc;
    }

    getRatio(src: string) {
        const width = src?.match(/\/(\d+x\d+)\//) || src?.match(/(\d+x\d+)(?=\.)/);
        const ratio = width ? width[1].replace('x', '/') : '';

        return ratio;
    }

    private handleImageLoad = (): void => {
        this.sewPictureComplete = true;
        this.sewPictureLoaded.emit();
    };

    @Method()
    async reloadImage(): Promise<void> {
        this.host.shadowRoot.querySelector('img').src = this.src;
    }

    public render() {
        return (
            <Host>
                <picture>
                    {this.mediaSources.length > 0
                        ? this.mediaSources.map(({ media, srcset }: { media: string; srcset: string }) => (
                              <source key={media} media={media} srcSet={srcset} />
                          ))
                        : null}
                    <img
                        class={{
                            'object-fit': this.objectFit,
                            'object-fit-contain': this.objectFitContain,
                        }}
                        src={this.src}
                        alt={this.alt}
                        title={this.imgTitle}
                        loading={this.showImage ? 'eager' : 'lazy'}
                        {...(this.width ? { width: this.width } : {})}
                        {...(this.height ? { height: this.height } : {})}
                        onLoad={this.handleImageLoad}
                        onError={this.handleImageLoad}
                        ref={el => (this.imgElement = el)}
                    />
                </picture>
                {this.caption && (
                    <p
                        class={{
                            'caption': true,
                            'shift-caption-right': this.shiftCaptionRight,
                            'shift-caption-right-mq3': this.shiftCaptionRightMq3,
                        }}
                    >
                        {this.caption}
                    </p>
                )}
            </Host>
        );
    }
}
